import AddIcon from "@mui/icons-material/Add";
import { Button, Divider, Grid, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import * as React from "react";
import { useState } from "react";
import { useRecordContext } from "react-admin";
import { Controller, useForm } from "react-hook-form";
import CustomEditRemoveIcon from "../../unit/CustomEditRemoveIcon";
import CustomProductTextField from "../../unit/forms/CustomProductTextField";
import useNotify from "../../unit/useNotify";
import SectionStyled from "./SectionStyled";
import SectionTitle from "./SectionTitle";
import CustomProductActionBottom from "../CustomProductActionBottom";

export default function Authorization({
  onSave,
  defaultValues,
  ...props
}: any) {
  const itemKeyRef = React.useRef(0);
  const record = useRecordContext();
  const { success, error, warning, info } = useNotify();
  const sectionName = "authorization-closing";
  const sectionTitle =
    "Authorization and Acknowledgement - Information that requires customer's review or approval";
  const sectionSubtitle = "";

  const defaultAuthorizationClosing = [
    {
      id: "",
      title: "",
      description: "",
    },
  ];

  const [authorizationClosing, setAuthorizationClosing] = useState(
    (
      record?.data?.basic?.authorization?.closing_disclosure ||
      defaultAuthorizationClosing
    ).map((item: any) => ({
      ...item,
      __clientKey: `authorization-closing-${itemKeyRef.current++}`,
    }))
  );
  const subType = record?.data?.basic?.subType;
  const division = record?.data?.basic?.division;
  const productModulesGroup = record?.data?.basic?.productModulesGroup;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: defaultValues || {},
  });

  React.useEffect(() => {
    if (record?.data?.basic?.authorization) {
      const nextAuthorizationClosing = (
        record.data.basic.authorization.closing_disclosure ||
        defaultAuthorizationClosing
      ).map((item: any) => ({
        ...item,
        __clientKey: `authorization-closing-${itemKeyRef.current++}`,
      }));

      setAuthorizationClosing(nextAuthorizationClosing);
      reset({
        authorizationClosing: nextAuthorizationClosing,
      });
    }
  }, [record, reset]);
  React.useEffect(() => {
    if (props?.onDirtyChange) {
      props?.onDirtyChange(props?.curMenu, isDirty);
    }
  }, [isDirty, reset]);

  const hasEmptyAuthorizationClosing = authorizationClosing.some(
    (item: any) => !item.title?.trim() || !item.description?.trim()
  );

  const buildDefaultAuthorizationClosing = () =>
    defaultAuthorizationClosing.map((item) => ({
      ...item,
      __clientKey: `authorization-closing-${itemKeyRef.current++}`,
    }));

  const sanitizeAuthorizationClosing = (items: any[]) =>
    items.map(({ __clientKey, ...item }) => item);

  const persistAuthorizationClosing = (nextAuthorizationClosing: any[]) => {
    const formData: any = getValues();
    formData.saveAction = "authorization";
    formData.saveActionType = "closingDisclosure";
    formData.authorizationClosing = sanitizeAuthorizationClosing(
      nextAuthorizationClosing
    );

    if (onSave) {
      onSave(formData, props?.curMenu);
      reset({
        ...formData,
        authorizationClosing: nextAuthorizationClosing,
      });
    }
  };

  const addAuthorizationClosing = () => {
    const newAuthorizationClosing = {
      id: "",
      title: "",
      description: "",
      __clientKey: `authorization-closing-${itemKeyRef.current++}`,
    };
    const nextAuthorizationClosing = [
      ...authorizationClosing,
      newAuthorizationClosing,
    ];

    setAuthorizationClosing(nextAuthorizationClosing);
    setValue("authorizationClosing", nextAuthorizationClosing, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const addDefaultAuthorizationClosing = () => {
    const nextAuthorizationClosing = [
      ...authorizationClosing,
      ...buildDefaultAuthorizationClosing(),
    ];
    setAuthorizationClosing(nextAuthorizationClosing);
    setValue("authorizationClosing", nextAuthorizationClosing, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const removeAcknowledgement = (index: any) => {
    if (authorizationClosing.length > 0) {
      const updated = [...authorizationClosing];
      updated.splice(index, 1);
      setAuthorizationClosing(updated);
      setValue("authorizationClosing", updated, { shouldDirty: true });
      persistAuthorizationClosing(updated);
    }
    success("Acknowledgement removed and saved.");
  };

  const handleSaveClick = () => {
    if (hasEmptyAuthorizationClosing) {
      warning("Title and Description cannot be empty.");
      return;
    }

    persistAuthorizationClosing(authorizationClosing);
  };

  const handleAuthorizationClosingChange = (
    index: any,
    field: any,
    value: any
  ) => {
    const updated = [...authorizationClosing];
    updated[index][field] = value;
    setAuthorizationClosing(updated);
    setValue(`authorizationClosing[${index}].${field}`, value, {
      shouldDirty: true,
    });
  };

  return (
    <SectionStyled id={"section_" + sectionName}>
      <SectionTitle subtitle={sectionSubtitle} title={sectionTitle} />

      <Box
        sx={{
          padding: "10px",
          height: "calc(100vh - 255px)",
          overflowY: "auto",
          // background: "yellow",
        }}
      >
        {productModulesGroup?.includes("disclosure") && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Closing
              </Typography>
              {authorizationClosing.map((item: any, index: any) => (
                <React.Fragment key={item.__clientKey}>
                  <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 3 }}
                  >
                    <Grid item xs={10}>
                      <Controller
                        name={`authorizationClosing[${index}].title`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            label="Title"
                            value={item.title}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAuthorizationClosingChange(
                                index,
                                "title",
                                e.target.value
                              );
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        type="hidden"
                        name={`authorizationClosing[${index}].id`}
                        value={item.id}
                        sx={{ display: "none" }}
                      />
                      {authorizationClosing.length > 0 && (
                        <CustomEditRemoveIcon
                          onClick={() => removeAcknowledgement(index)}
                          tooltip="Remove this acknowledgement"
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name={`authorizationClosing[${index}].description`}
                        control={control}
                        render={({ field }) => (
                          <CustomProductTextField
                            {...field}
                            fullWidth
                            multiline
                            rows={4}
                            variant="outlined"
                            label="Description"
                            value={item.description}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAuthorizationClosingChange(
                                index,
                                "description",
                                e.target.value
                              );
                            }}
                            sx={{ marginTop: "-4px" }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {index < authorizationClosing.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </React.Fragment>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addAuthorizationClosing}
                sx={{ width: "120px" }}
              >
                Add
              </Button>
              {/* <Button
                variant="outlined"
                onClick={addDefaultAuthorizationClosing}
                sx={{ width: "140px", ml: 2 }}
              >
                Use Default
              </Button> */}
            </Grid>
          </Grid>
        )}
      </Box>
      {/* Form Actions */}
      <CustomProductActionBottom>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          disabled={!isDirty || hasEmptyAuthorizationClosing}
          sx={{ width: "120px", mt: 1 }}
        >
          Save
        </Button>
      </CustomProductActionBottom>
    </SectionStyled>
  );
}
